/**
 * Login page component
 */

import React, { useState } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { LoginRequest, RegisterRequest } from '../types';
import { HelpCircle } from 'lucide-react';

export function Login() {
  const { login, register, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const [isRegistering, setIsRegistering] = useState(false);
  
  // Login form state
  const [credentials, setCredentials] = useState<LoginRequest>({
    username: '',
    password: '',
  });
  
  // Registration form state
  const [registerData, setRegisterData] = useState<RegisterRequest>({
    username: '',
    email: '',
    password: '',
    confirm_password: '',
  });
  
  const [error, setError] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Redirect if already authenticated
  if (isAuthenticated) {
    const from = (location.state as any)?.from?.pathname || '/dashboard';
    return <Navigate to={from} replace />;
  }

  const validateRegistration = (data: RegisterRequest): Record<string, string> => {
    const errors: Record<string, string> = {};
    
    if (data.username.length < 3 || data.username.length > 50) {
      errors.username = 'Username must be 3-50 characters';
    }
    
    if (!/^[a-zA-Z0-9_]+$/.test(data.username)) {
      errors.username = 'Username must contain only alphanumeric characters and underscores';
    }
    
    if (data.password.length < 12) {
      errors.password = 'Password must be at least 12 characters';
    }
    
    if (data.password !== data.confirm_password) {
      errors.confirm_password = 'Passwords do not match';
    }
    
    return errors;
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await login(credentials);
      // Navigation will happen automatically via the auth context
    } catch (error: any) {
      setError(error.message || 'Login failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setSubmitting(true);

    // Client-side validation
    const errors = validateRegistration(registerData);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSubmitting(false);
      return;
    }

    try {
      await register(registerData);
      // Navigation will happen automatically via the auth context
    } catch (error: any) {
      if (error.response?.data?.detail) {
        setError(error.response.data.detail);
      } else {
        setError(error.message || 'Registration failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRegisterData(prev => ({ ...prev, [name]: value }));
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            {isRegistering ? 'Create your account' : 'Sign in to Do-Labs'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-400">
            Linux training platform with hands-on labs
          </p>
          <div className="mt-3 text-center">
            <Link 
              to="/userdocs" 
              className="inline-flex items-center text-sm text-blue-400 hover:text-blue-300 transition-colors"
            >
              <HelpCircle className="w-4 h-4 mr-1" />
              Need help? View documentation
            </Link>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 rounded-lg bg-gray-800 p-1">
          <button
            data-testid="login-tab"
            onClick={() => setIsRegistering(false)}
            className={`flex-1 rounded-md py-2 px-3 text-sm font-medium transition-colors ${
              !isRegistering
                ? 'bg-blue-600 text-white shadow'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            Sign In
          </button>
          <button
            data-testid="register-tab"
            onClick={() => setIsRegistering(true)}
            className={`flex-1 rounded-md py-2 px-3 text-sm font-medium transition-colors ${
              isRegistering
                ? 'bg-blue-600 text-white shadow'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            Sign Up
          </button>
        </div>
        
        {/* Login Form */}
        {!isRegistering && (
          <form data-testid="login-form" className="mt-8 space-y-6" onSubmit={handleLoginSubmit}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="username" className="sr-only">
                  Username or Email
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-600 placeholder-gray-400 text-white bg-gray-800 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Username or Email"
                  value={credentials.username}
                  onChange={handleLoginChange}
                  disabled={submitting}
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-600 placeholder-gray-400 text-white bg-gray-800 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Password"
                  value={credentials.password}
                  onChange={handleLoginChange}
                  disabled={submitting}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={submitting}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>
        )}

        {/* Registration Form */}
        {isRegistering && (
          <form data-testid="registration-form" className="mt-8 space-y-6" onSubmit={handleRegisterSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="reg-username" className="sr-only">
                  Username
                </label>
                <input
                  id="reg-username"
                  name="username"
                  type="text"
                  required
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-600 placeholder-gray-400 text-white bg-gray-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Username"
                  value={registerData.username}
                  onChange={handleRegisterChange}
                  disabled={submitting}
                />
                {fieldErrors.username && (
                  <p data-testid="username-error" className="mt-1 text-sm text-red-400">{fieldErrors.username}</p>
                )}
              </div>

              <div>
                <label htmlFor="reg-email" className="sr-only">
                  Email
                </label>
                <input
                  id="reg-email"
                  name="email"
                  type="email"
                  required
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-600 placeholder-gray-400 text-white bg-gray-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Email address"
                  value={registerData.email}
                  onChange={handleRegisterChange}
                  disabled={submitting}
                />
              </div>

              <div>
                <label htmlFor="reg-password" className="sr-only">
                  Password
                </label>
                <input
                  id="reg-password"
                  name="password"
                  type="password"
                  required
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-600 placeholder-gray-400 text-white bg-gray-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Password (12+ characters)"
                  value={registerData.password}
                  onChange={handleRegisterChange}
                  disabled={submitting}
                />
                {fieldErrors.password && (
                  <p data-testid="password-error" className="mt-1 text-sm text-red-400">{fieldErrors.password}</p>
                )}
              </div>

              <div>
                <label htmlFor="reg-confirm-password" className="sr-only">
                  Confirm Password
                </label>
                <input
                  id="reg-confirm-password"
                  name="confirm_password"
                  type="password"
                  required
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-600 placeholder-gray-400 text-white bg-gray-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Confirm password"
                  value={registerData.confirm_password}
                  onChange={handleRegisterChange}
                  disabled={submitting}
                />
                {fieldErrors.confirm_password && (
                  <p data-testid="confirm-password-error" className="mt-1 text-sm text-red-400">{fieldErrors.confirm_password}</p>
                )}
              </div>
            </div>

            <div>
              <button
                data-testid="register-submit-button"
                type="submit"
                disabled={submitting}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Creating account...' : 'Create account'}
              </button>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-400">
                New learner accounts are created by default
              </p>
            </div>
          </form>
        )}

        {/* Error Display */}
        {error && (
          <div data-testid="api-error" className="rounded-md bg-red-900 border border-red-700 p-4">
            <div className="text-sm text-red-200">{error}</div>
          </div>
        )}
      </div>
    </div>
  );
}